<ng-template #contextNotes>
  <div class="flex flex-col h-full min-h-0 gap-3">
    @if (showSyncAllBanner) {
      <app-sync-all-banner
        class="shrink-0"
        [disabled]="syncAllDisabled"
        (syncAll)="syncAll.emit()" />
    }

    <div
      class="flex-1 h-0 min-h-0 overflow-y-auto flex flex-col gap-2"
      [appScrollToBottom]="notes.length">
      @if (!isLegalOrg) {
        <app-details-sections
          variant="document"
          [copilot]="copilot"
          [detailsBoxClasses]="detailsBoxClasses"
          [textClass]="detailsFieldClasses"
          [emptyClass]="detailsPlaceholderClasses"
          personalDetailsTestId="content-panel-personal-details"
          visitDetailsTestId="content-panel-visit-details" />
      }
      @if (!hideContextNotes) {
        <div class="flex-1 min-h-0" aria-hidden="true"></div>
        <app-collapsible-list
          class="shrink-0"
          [title]="'Added context' | translate"
          [items]="contextItems"
          [emptyText]="'No content added' | translate"
          [expanded]="true"
          [removable]="!contextReadonly"
          [striped]="true"
          [classes]="addedContextClasses"
          (remove)="onRemoveFromList($event)"
          data-testid="content-panel-added-context" />
      }
    </div>

    @if (!hideContextNotes) {
      <div
        class="shrink-0 rounded-[7px] border border-surface-dim bg-white px-5 py-4"
        [class.opacity-50]="contextReadonly"
        data-testid="content-panel-composer">
        <label class="sr-only" for="document-content-draft">{{ 'Type a note' | translate }}</label>
        <textarea
          id="document-content-draft"
          class="body-medium w-full min-h-[56px] resize-none bg-transparent border-0 outline-none text-on-surface placeholder:text-on-surface-variant disabled:cursor-not-allowed"
          [(ngModel)]="noteDraft"
          [disabled]="contextReadonly"
          [placeholder]="('Type a note' | translate) + '…'"
          (keydown)="onNoteKeydown($event)"
          data-testid="content-panel-input"></textarea>
      </div>
    }
  </div>
</ng-template>

@if (isMobileSheet) {
  <app-bottom-sheet
    #bottomSheet
    [collapsedHeightPx]="collapsedHeightPx"
    [handleAriaLabel]="'Expand or collapse content panel' | translate"
    [backdropAriaLabel]="'Collapse content panel' | translate"
    (stateChange)="onSheetStateChange($event)">

    <div class="shrink-0">
      <mat-chip-listbox
        class="content-panel-chips px-3 pb-2"
        [hideSingleSelectionIndicator]="true"
        [value]="selectedMobileView"
        (change)="onChipsChange($event.value)"
      >
        <mat-chip-option
          value="transcript"
          class="flex-1"
          (selectionChange)="keepSelected($event)">
          {{ 'Transcript' | translate }}
        </mat-chip-option>

        <mat-chip-option
          value="context"
          class="flex-1"
          (selectionChange)="keepSelected($event)">
          {{ 'Context' | translate }}
        </mat-chip-option>
      </mat-chip-listbox>
    </div>

    <div
      class="flex flex-1 min-h-0 flex-col overflow-hidden"
      data-testid="content-panel-mobile-body">

      <!-- Context -->
      <div
        class="grid min-h-0"
        [ngClass]="[
          selectedMobileView === 'context'
            ? 'flex-1 grid-rows-[1fr]'
            : 'grid-rows-[0fr]',
          isExpanded
            ? 'transition-[grid-template-rows] duration-600 ease-in-out motion-reduce:transition-none'
            : 'transition-none'
        ]">

        <div
          class="min-h-0"
          [ngClass]="[
            selectedMobileView === 'context'
              ? 'opacity-100 translate-x-0 overflow-hidden'
              : 'opacity-0 -translate-x-2 pointer-events-none overflow-hidden',
            isExpanded
              ? 'transition-[opacity,transform] duration-600 ease-in-out motion-reduce:transition-none'
              : 'transition-none'
          ]">

          <div class="flex flex-col h-full min-h-0 pt-2 pb-6 px-3 bg-surface-container-low">
            <ng-container *ngTemplateOutlet="contextNotes" />
          </div>
        </div>
      </div>

      <!-- Transcript -->
      <div
        class="grid min-h-0"
        [ngClass]="[
          selectedMobileView === 'transcript'
            ? 'flex-1 grid-rows-[1fr]'
            : 'grid-rows-[0fr]',
          isExpanded
            ? 'transition-[grid-template-rows] duration-600 ease-in-out motion-reduce:transition-none'
            : 'transition-none'
        ]">

        <div
          class="h-full min-h-0 overflow-hidden"
          [ngClass]="[
            selectedMobileView === 'transcript'
              ? 'opacity-100 translate-x-0'
              : 'opacity-0 translate-x-2 pointer-events-none',
            isExpanded
              ? 'transition-[opacity,transform] duration-600 ease-in-out motion-reduce:transition-none'
              : 'transition-none'
          ]">

          <app-transcript-list
            class="h-full"
            [utterances]="utterances"
            [deleted]="transcriptDeleted"
            variant="light" />
        </div>
      </div>

    </div>
  </app-bottom-sheet>
} @else {
  <div 
    class="relative flex flex-col flex-1 min-h-0 h-full"
    [ngClass]="{'bg-surface' : desktopCollapsed }"
    (click)="desktopCollapsed && desktopToggleCollapse()">
    <div
      class="absolute flex items-center justify-center cursor-pointer
        h-11 w-7 z-10 mt-1.25 rounded-r-lg
        bg-surface-bright border border-l-0 border-surface-dim
        transition-colors duration-200 hover:bg-surface-container-low motion-reduce:transition-none"
      role="button"
      tabindex="0"
      (click)="$event.stopPropagation(); desktopToggleCollapse()"
      (keydown.enter)="desktopToggleCollapse()"
      (keydown.space)="$event.preventDefault(); desktopToggleCollapse()"
      [attr.aria-label]="(desktopCollapsed ? 'Show content panel' : 'Hide content panel') | translate"
      data-testid="content-panel-collapse">
      @if (desktopCollapsed) {
        <mat-icon svgIcon="nav-arrow-left" class="icon-size [--icon-size:20px] text-[#8A9296]!"></mat-icon>
      } @else {
        <mat-icon svgIcon="nav-arrow-right" class="icon-size [--icon-size:20px] text-[#8A9296]!"></mat-icon>
      }
    </div>

    <!-- Expanded content panel -->
    <div class="relative grid flex-1 min-h-0 grid-cols-1 grid-rows-1">
      <div
        class="col-start-1 row-start-1 min-h-0 transition-opacity duration-300 ease-in-out motion-reduce:transition-none"
        [class.opacity-0]="desktopCollapsed"
        [class.pointer-events-none]="desktopCollapsed"
        [attr.inert]="desktopCollapsed ? '' : null"
        [attr.aria-hidden]="desktopCollapsed">
        <mat-tab-group
          [mat-stretch-tabs]="true"
          [selectedIndex]="selectedDesktopView === 'transcript' ? 0 : 1"
          (selectedTabChange)="onDesktopTabChange($event.index)"
          class="content-panel-tabs md:min-h-0 flex-1 h-full flex flex-col
            [&_.mat-mdc-tab-header]:bg-surface
            [&_.mat-mdc-tab-body-wrapper]:flex-1
            [&_.mat-mdc-tab-body-wrapper]:min-h-0
            [&_.mat-mdc-tab-body]:h-full
            [&_.mat-mdc-tab-body-content]:flex
            [&_.mat-mdc-tab-body-content]:flex-col
            [&_.mat-mdc-tab-body-content]:h-full
            [&_.mat-mdc-tab-body-content]:overflow-hidden">
          <mat-tab>
            <ng-template mat-tab-label>
              <span class="flex items-center gap-2" [class.text-on-surface-variant]="selectedDesktopView !== 'transcript'">
                <mat-icon svgIcon="page-flip" class="icon-size [--icon-size:24px] text-on-surface-variant!"></mat-icon>
                <span>{{ 'Transcript' | translate }}</span>
              </span>
            </ng-template>
            <app-transcript-list class="h-full" [utterances]="utterances" [deleted]="transcriptDeleted" variant="light" />
          </mat-tab>
          <mat-tab>
            <ng-template mat-tab-label>
              <span class="flex items-center gap-2" [class.text-on-surface-variant]="selectedDesktopView !== 'context'">
                <mat-icon svgIcon="medium" class="icon-size [--icon-size:24px] text-on-surface-variant!"></mat-icon>
                <span>{{ 'Context' | translate }}</span>
              </span>
            </ng-template>
            <div class="flex flex-col h-full min-h-0 pt-2 pb-6 px-3 gap-2 bg-surface-container-low md:bg-white">
              <ng-container *ngTemplateOutlet="contextNotes" />
            </div>
          </mat-tab>
        </mat-tab-group>
      </div>

      <!-- Collapsed Icons -->
      <div
        class="col-start-1 row-start-1 flex w-full flex-col items-center gap-3 pt-15 transition-opacity duration-300 ease-in-out motion-reduce:transition-none"
        [class.opacity-0]="!desktopCollapsed"
        [class.pointer-events-none]="!desktopCollapsed"
        [attr.inert]="!desktopCollapsed ? '' : null"
        [attr.aria-hidden]="!desktopCollapsed">
        <div class="h-px shrink-0 self-stretch bg-surface-dim mx-2"></div>
        <button
          mat-icon-button
          [attr.aria-label]="'Show Transcript' | translate"
          (click)="onCollapsedIconClick('transcript')">
          <mat-icon svgIcon="page-flip"></mat-icon>
        </button>
        <div class="h-px shrink-0 self-stretch bg-surface-dim mx-2"></div>
        <button
          mat-icon-button
          [attr.aria-label]="'Show Context' | translate"
          (click)="onCollapsedIconClick('context')">
          <mat-icon svgIcon="medium"></mat-icon>
        </button>
        <div class="h-px shrink-0 self-stretch bg-surface-dim mx-2"></div>
      </div>
    </div>
  </div>
}